import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, FireExtinguisher } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

const TYPE_LABEL: Record<string, string> = {
  ABC: 'ABC dry chemical',
  BC:  'BC (grease & flammable liquid)',
  K:   'Class K (kitchen)',
  CO2: 'CO₂',
};

function extAge(ext: FireExtinguisher): number | null {
  if (!ext.purchase_year) return null;
  return CURRENT_YEAR - ext.purchase_year;
}

function ageBadge(age: number | null): { label: string; color: string; bg: string } | null {
  if (age === null) return null;
  if (age >= 12) return { label: `${age}y — Replace/test`, color: '#b91c1c', bg: '#fef2f2' };
  if (age >= 10) return { label: `${age}y — Inspect soon`, color: '#c2410c', bg: '#fff7ed' };
  return { label: `${age}y old`, color: '#374151', bg: '#f9fafb' };
}

function lastInspectedWarning(lastInspected: string | null): boolean {
  if (!lastInspected) return true;
  const d = new Date(lastInspected);
  const diff = (Date.now() - d.getTime()) / 86400000;
  return diff > 365;
}

export function FireExtinguishers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const home = (homesData?.data ?? []).find(h => h.is_primary) ?? homesData?.data?.[0];

  const { data: extsData, isLoading } = useQuery({
    queryKey: ['fire-extinguishers', home?.id],
    queryFn: () => api.get<{ data: FireExtinguisher[] }>(`/homes/${home!.id}/fire-extinguishers`),
    enabled: !!home,
  });
  const exts = extsData?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/fire-extinguishers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fire-extinguishers', home?.id] });
      setConfirmDelete(null);
    },
  });

  const overdueCount   = exts.filter(e => lastInspectedWarning(e.last_inspected)).length;
  const pastLifeCount  = exts.filter(e => (extAge(e) ?? 0) >= 12).length;

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 100 }}>
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}
        >
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff' }}>Fire Extinguishers</h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
              {exts.length} extinguisher{exts.length !== 1 ? 's' : ''} tracked
            </p>
          </div>
          <button
            onClick={() => navigate('/app/fire-extinguishers/add')}
            style={{
              background: '#fff', color: '#1D4ED8', border: 'none',
              borderRadius: 10, padding: '10px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            + Add
          </button>
        </div>
      </div>

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Warnings banner */}
        {(pastLifeCount > 0 || overdueCount > 0) && (
          <div style={{
            padding: '14px 16px', background: pastLifeCount > 0 ? '#fef2f2' : '#fffbeb',
            borderRadius: 12, border: `1.5px solid ${pastLifeCount > 0 ? '#fecaca' : '#fde68a'}`,
          }}>
            {pastLifeCount > 0 && (
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#b91c1c' }}>
                {pastLifeCount} extinguisher{pastLifeCount > 1 ? 's' : ''} past 12-year lifespan
              </p>
            )}
            {overdueCount > 0 && (
              <p style={{ margin: pastLifeCount > 0 ? '4px 0 0' : 0, fontWeight: 500, fontSize: 14, color: '#92400e' }}>
                {overdueCount} not visually inspected in over a year
              </p>
            )}
          </div>
        )}

        {/* Recommended locations */}
        {exts.length === 0 && !isLoading && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
            <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 15, color: '#111827' }}>
              Recommended locations
            </p>
            {[
              'Kitchen (ABC or K-class)',
              'Garage',
              'Each floor / hallway',
              'Master bedroom',
              'Near fireplace or wood stove',
              'Basement mechanical room',
            ].map(loc => (
              <p key={loc} style={{ margin: '4px 0', fontSize: 14, color: '#374151' }}>• {loc}</p>
            ))}
          </div>
        )}

        {isLoading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>
        )}

        {exts.map(ext => {
          const age   = extAge(ext);
          const badge = ageBadge(age);
          const needsInspection = lastInspectedWarning(ext.last_inspected);

          return (
            <div key={ext.id} style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 16, color: '#111827' }}>
                    {ext.location}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                    {ext.type ? TYPE_LABEL[ext.type] : 'Unknown type'}
                    {ext.size_lbs ? ` · ${ext.size_lbs} lbs` : ''}
                    {ext.purchase_year ? ` · Purchased ${ext.purchase_year}` : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  {badge && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                      background: badge.bg, color: badge.color,
                    }}>
                      {badge.label}
                    </span>
                  )}
                  {needsInspection && (
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                      background: '#fffbeb', color: '#92400e',
                    }}>
                      Inspect overdue
                    </span>
                  )}
                </div>
              </div>

              {(ext.last_inspected || ext.last_recharged) && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {ext.last_inspected && (
                    <span style={{ fontSize: 12, color: '#6b7280', background: '#f9fafb', padding: '4px 8px', borderRadius: 6 }}>
                      Last inspected: {ext.last_inspected}
                    </span>
                  )}
                  {ext.last_recharged && (
                    <span style={{ fontSize: 12, color: '#6b7280', background: '#f9fafb', padding: '4px 8px', borderRadius: 6 }}>
                      Last recharged: {ext.last_recharged}
                    </span>
                  )}
                </div>
              )}

              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setConfirmDelete(ext.id)}
                  style={{
                    flex: 1, padding: '8px', background: '#fef2f2', color: '#ef4444',
                    border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500,
                  }}
                >
                  Remove
                </button>
              </div>

              {confirmDelete === ext.id && (
                <div style={{ marginTop: 10, padding: '12px', background: '#fef2f2', borderRadius: 8 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>
                    Remove {ext.location} extinguisher?
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => deleteMutation.mutate(ext.id)}
                      disabled={deleteMutation.isPending}
                      style={{ flex: 1, padding: '8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                    >
                      {deleteMutation.isPending ? 'Removing…' : 'Yes, remove'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      style={{ flex: 1, padding: '8px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!isLoading && exts.length === 0 && (
          <button
            onClick={() => navigate('/app/fire-extinguishers/add')}
            style={{
              padding: '16px', background: '#eff6ff', color: '#1D4ED8',
              border: '2px dashed #bfdbfe', borderRadius: 12,
              fontWeight: 600, fontSize: 15, cursor: 'pointer', width: '100%',
            }}
          >
            + Add your first fire extinguisher
          </button>
        )}
      </div>

      <NavBar />
    </div>
  );
}
